import { Trans } from "@web-monorepo/i18n/macro";
import { useRouteError } from "react-router-dom";
import type { ReactNode } from "react";
import { DDSHeadlineText } from "@web-monorepo/dds";
import { ErrorPage } from "./ErrorPage";

import { captureException, EnrichedError } from "@web-monorepo/telemetry";

export interface ReactRouterErrorBoundaryProps {
  error?: unknown;
  children?: ReactNode;
  noReloadButton?: boolean;
  reloadButtonText?: ReactNode;
}

class ErrorGettingErrorError extends Error implements EnrichedError {
  cause?: unknown;

  constructor(x: unknown) {
    if (x instanceof Error) {
      // This is a truly unusual error – we're catching an error while trying to handle another error!
      super(`Error getting error in ReactRouterErrorBoundary: ${x.message}`);
    } else {
      super(`Error getting error in ReactRouterErrorBoundary: ${String(x)}`);
    }

    this.cause = x;
  }
}

export function ReactRouterErrorBoundary({
  error: providedError,
  children,
  noReloadButton,
  reloadButtonText,
}: ReactRouterErrorBoundaryProps) {
  let routeError: unknown;
  try {
    const getRouteError: () => unknown = useRouteError as unknown as () => unknown;
    routeError = getRouteError();
  } catch (x) {
    captureException(new ErrorGettingErrorError(x));

    if (!(x instanceof Error)) {
      throw x;
    }

    routeError = x;
  }

  const error = providedError ?? routeError;

  return (
    <ErrorPage error={error} noReloadButton={noReloadButton} reloadButtonText={reloadButtonText}>
      {children ?? (
        <div>
          <DDSHeadlineText level="md">
            <Trans>We encountered an unexpected error.</Trans>
          </DDSHeadlineText>
        </div>
      )}
    </ErrorPage>
  );
}
